import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import { CategoryCardInput, KPITrendComponentInput } from '../../models/vsc-scorecard.model';

type ScoreBand = 'poor' | 'average' | 'good' | 'excellent';

interface KpiRow {
  name: string;
  score: number | null;
  maxScore: number;
  progressPct: number;
  band: ScoreBand;
}

interface CategoryRow {
  id: number;
  name: string;
  score: number;
  maxScore: number;
  progressPct: number;
  band: ScoreBand;
  expanded: boolean;
  kpis: KpiRow[];
}

@Component({
  selector: 'app-category-performance-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './category-performance-card.component.html',
  styleUrl: './category-performance-card.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryPerformanceCardComponent {
  @Input({ required: true }) categories: CategoryCardInput[] = [];
  @Input() isShopView = false;
  @Input() isLoading = false;

  private readonly expandedIds = signal<Set<number>>(new Set<number>());

  get rows(): CategoryRow[] {
    const expanded = this.expandedIds();
    return this.categories.map(cat => {
      const band = this.band(cat.outOfFiveScore);
      const score = this.isShopView ? cat.outOfFiveScore : cat.outOfTotalScore;
      const maxScore = this.isShopView ? (cat.maxOutOfFiveScore ?? 5) : cat.maxOutOfTotalScore;
      return {
        id: cat.categoryId,
        name: cat.categoryName,
        score,
        maxScore,
        progressPct: maxScore > 0 ? (score / maxScore) * 100 : 0,
        band,
        expanded: expanded.has(cat.categoryId),
        kpis: cat.kpis.filter(k => k.kpiWeight > 0).map(k => this.kpiRow(k)),
      };
    });
  }

  toggle(id: number): void {
    this.expandedIds.update(set => {
      const next = new Set(set);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  expandAll(): void {
    this.expandedIds.set(new Set(this.categories.map(c => c.categoryId)));
  }

  collapseAll(): void {
    this.expandedIds.set(new Set<number>());
  }

  /** Snapshot of which rows are open, so a download can put them back afterwards. */
  expansionState(): Set<number> {
    return new Set(this.expandedIds());
  }

  restoreExpansion(state: Set<number>): void {
    this.expandedIds.set(new Set(state));
  }

  get cardBand(): ScoreBand {
    if (!this.categories.length) return 'good';
    const avg = this.categories.reduce((s, c) => s + c.outOfFiveScore, 0) / this.categories.length;
    return this.band(avg);
  }

  scoreClass(b: ScoreBand): string { return `score--${b}`; }
  fillClass(b: ScoreBand): string  { return `score--${b}-fill`; }
  textClass(b: ScoreBand): string  { return `score--${b}-text`; }

  private kpiRow(kpi: KPITrendComponentInput): KpiRow {
    // Only the selected month (last entry) — no fallback to earlier months
    const last = kpi.trends.at(-1);
    const outOfFive = last?.trendsOutOfFiveDataThisYear ?? null;
    const weighted = last?.trendsWeightedDataThisYear ?? null;
    let score: number | null = null;
    if (outOfFive !== null) {
      score = this.isShopView ? outOfFive : (weighted ?? null);
    }
    const maxScore = this.isShopView ? (kpi.kpiMaxOutofFiveScore ?? 5) : (kpi.kpiMaxWeightedScore ?? kpi.kpiWeight);
    // For band colour, use last available data so the row still has meaningful colour
    const bandBase = ([...kpi.trends].reverse().find(t => t.trendsOutOfFiveDataThisYear !== null)?.trendsOutOfFiveDataThisYear) ?? 0;
    return {
      name: kpi.kpiName,
      score,
      maxScore,
      progressPct: score !== null && maxScore > 0 ? (score / maxScore) * 100 : 0,
      band: this.band(bandBase),
    };
  }

  private band(outOfFive: number): ScoreBand {
    if (outOfFive < 2) return 'poor';
    if (outOfFive < 3) return 'average';
    if (outOfFive <= 4.5) return 'good';
    return 'excellent';
  }
}
